import { Button as AntdButton } from 'antd';
import classNames from 'classnames';
import { ComponentProps, FC } from 'react';
import styles from './style.module.scss';
import Link from 'next/link';

type SizeType = 'S' | 'M' | 'L';

interface Props extends Omit<ComponentProps<typeof AntdButton>, 'variant' | 'shape' | 'size'> {
  variant?: 'fill' | 'grey' | 'premium' | 'premiumSecondary' | 'bubble-outline';
  round?: boolean;
  size?: string;
  className?: string;
  disabled?: boolean;
  href?: string;
  target?: string;
  prefetch?: boolean;
}

const Button: FC<Props> = ({
  variant = 'fill',
  size = 'M',
  round,
  className,
  disabled,
  href,
  target = '_self',
  prefetch,
  ...props
}) => {
  const content = (
    <AntdButton
      className={classNames(
        {
          [styles.variant__fill]: variant === 'fill',
          [styles.variant__grey]: variant === 'grey',
          [styles.variant__premium]: variant === 'premium',
          [styles.variant__premiumSecondary]: variant === 'premiumSecondary',
          [styles.variant__bubbleOutline]: variant === 'bubble-outline',
          [styles.type__round]: round,
          [styles.type__square]: !round,
          ...getSizeClasses(size),
        },
        styles.button,
        className,
      )}
      disabled={disabled}
      {...props}>
      {props.children}
    </AntdButton>
  );

  if (href) {
    return (
      <Link href={href} target={target} prefetch={prefetch} passHref>
        {content}
      </Link>
    );
  }

  return content;
};

export default Button;

const getSizeClasses = (size: string) => {
  const sizeClasses: Record<string, boolean> = {};

  const sizeParts = size.split(' ');
  let defaultSize: SizeType = 'M';

  const breakpointSizes: Record<string, SizeType> = {};

  sizeParts.forEach((part) => {
    if (part.includes(':')) {
      const [breakpoint, bpSize] = part.split(':');
      if (bpSize && ['S', 'M', 'L'].includes(bpSize as SizeType)) {
        breakpointSizes[breakpoint] = bpSize as SizeType;
      }
    } else if (['S', 'M', 'L'].includes(part as SizeType)) {
      defaultSize = part as SizeType;
    }
  });

  sizeClasses[styles[`size__${defaultSize}`]] = true;

  Object.entries(breakpointSizes).forEach(([breakpoint, bpSize]) => {
    sizeClasses[styles[`size__${bpSize}__${breakpoint}`]] = true;
  });

  return sizeClasses;
};
